import { useEffect, useState } from 'react';

interface UseThumbnailGeneratorOptions {
    src: string;
    duration: number;
    thumbnailCount: number;
    cachedThumbnails?: string[];
    onThumbnailsGenerated?: (thumbnails: string[]) => void;
}

export function useThumbnailGenerator({
    src,
    duration,
    thumbnailCount,
    cachedThumbnails,
    onThumbnailsGenerated,
}: UseThumbnailGeneratorOptions): string[] {
    const [thumbnails, setThumbnails] = useState<string[]>([]);

    useEffect(() => {
        setThumbnails([]);

        if (cachedThumbnails && cachedThumbnails.length === thumbnailCount) {
            setThumbnails(cachedThumbnails);
            return;
        }

        let cancelled = false;
        let video: HTMLVideoElement | null = null;
        let metadataListener: (() => void) | null = null;
        let errorListener: ((e: Event) => void) | null = null;

        const generateThumbnails = async () => {
            if (!src || duration <= 0) return;

            try {
                video = document.createElement('video');
                // Must be set before `src` so the canvas isn't tainted when the
                // video is served cross-origin (e.g. the engine API on a
                // different subdomain in staging/prod). Without this,
                // `canvas.toDataURL()` throws a SecurityError and every
                // thumbnail comes back empty. Matches the credentialed media
                // requests used by the player so the browser can reuse them.
                video.crossOrigin = 'use-credentials';
                video.preload = 'metadata';
                video.muted = true;
                video.playsInline = true;
                video.src = src;

                const interval = duration / thumbnailCount;
                const canvas = document.createElement('canvas');
                canvas.width = 80;
                canvas.height = 45;
                const ctx = canvas.getContext('2d');

                if (!ctx) {
                    console.error('Failed to get canvas context');
                    return;
                }

                await new Promise<void>((resolve, reject) => {
                    if (!video) {
                        reject(new Error('Video element not created'));
                        return;
                    }

                    metadataListener = () => {
                        if (cancelled) return;
                        metadataListener = null;
                        errorListener = null;
                        resolve();
                    };

                    errorListener = (e: Event) => {
                        if (cancelled) {
                            metadataListener = null;
                            errorListener = null;
                            resolve();
                            return;
                        }
                        console.warn(
                            'Video metadata load failed - will retry on re-mount',
                        );
                        metadataListener = null;
                        errorListener = null;
                        resolve();
                    };

                    video.addEventListener('loadedmetadata', metadataListener, {
                        once: true,
                    });
                    video.addEventListener('error', errorListener, {
                        once: true,
                    });
                });

                if (cancelled) return;

                const captureThumbnailAt = async (
                    index: number,
                ): Promise<string> => {
                    if (cancelled || !video) return '';

                    const time = Math.min(
                        index * interval,
                        video.duration - 0.1,
                    );

                    try {
                        await new Promise<void>((resolve) => {
                            if (!video) {
                                resolve();
                                return;
                            }

                            if (video.readyState < 2) {
                                resolve();
                                return;
                            }

                            let resolved = false;
                            const cleanup = () => {
                                if (!video) return;
                                video.removeEventListener('seeked', onSeeked);
                                video.removeEventListener('error', onError);
                            };

                            const onSeeked = () => {
                                if (cancelled || resolved) return;
                                resolved = true;
                                cleanup();
                                resolve();
                            };

                            const onError = () => {
                                if (cancelled || resolved) return;
                                resolved = true;
                                cleanup();
                                resolve();
                            };

                            video.addEventListener('seeked', onSeeked);
                            video.addEventListener('error', onError);

                            try {
                                video.currentTime = time;
                            } catch (e) {
                                cleanup();
                                resolve();
                            }
                        });

                        if (cancelled || !video) return '';

                        if (
                            video.videoWidth > 0 &&
                            video.videoHeight > 0 &&
                            video.readyState >= 2
                        ) {
                            ctx.drawImage(
                                video,
                                0,
                                0,
                                canvas.width,
                                canvas.height,
                            );
                            return canvas.toDataURL('image/jpeg', 0.7);
                        }

                        return '';
                    } catch (error) {
                        return '';
                    }
                };

                const thumbnails = await Array.from({
                    length: thumbnailCount,
                }).reduce<Promise<string[]>>(
                    async (accPromise, _, i) => {
                        const acc = await accPromise;
                        if (cancelled) return acc;

                        const thumbnail = await captureThumbnailAt(i);
                        return [...acc, thumbnail];
                    },
                    Promise.resolve([] as string[]),
                );

                if (!cancelled && thumbnails.length > 0) {
                    setThumbnails(thumbnails);
                    onThumbnailsGenerated?.(thumbnails);
                }
            } catch (error) {
                console.error('Failed to generate thumbnails:', error);
            }
        };

        generateThumbnails().catch((error) => {
            console.error('Thumbnail generation failed:', error);
        });

        return () => {
            cancelled = true;

            if (video) {
                if (metadataListener) {
                    video.removeEventListener(
                        'loadedmetadata',
                        metadataListener,
                    );
                    metadataListener = null;
                }
                if (errorListener) {
                    video.removeEventListener('error', errorListener);
                    errorListener = null;
                }

                video.src = '';
                video.remove();
                video = null;
            }
        };
    }, [
        src,
        duration,
        thumbnailCount,
        cachedThumbnails,
        onThumbnailsGenerated,
    ]);

    return thumbnails;
}
